<script setup>
/**
 * FormControlUsersGroupsRoles
 * Organizes and initializes the permission management interface.
 * * Logic:
 * - Scans system components to extract unique 'rolesGroup' strings.
 * - Ensures the 'userRoles' store module is initialized for the specific role ID.
 * - Presets the role data into the local form state so changes are tracked for saving.
 */
import { computed, onMounted, getCurrentInstance } from 'vue';
import FormControlUsersGroupsRolesGroup from './FormControlUsersGroupsRolesGroup.vue';

const props = defineProps({
    formId: {
        type: String,
        required: true
    },
    field: Object,
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- Computed ---

const components = computed(() => store.state.components.items);

/**
 * Extracts a unique list of Roles Groups from all registered components.
 * Uses a Set to ensure uniqueness efficiently.
 */
const rolesGroups = computed(() => {
    const groups = new Set();

    components.value.forEach((obj) => {
        if (obj.roles && obj.rolesGroup) {
            groups.add(obj.rolesGroup);
        }
    });

    return Array.from(groups);
});

// --- Lifecycle (Logic from created()) ---

onMounted(async () => {
    const roleId = store.state[props.formId]?.id;

    let privileges = roleId ? store.state.userRoles.privileges[roleId] : undefined;

    // Initialize the role in the store if it doesn't exist
    if (privileges === undefined) {
        // Inserting a new role
        if (roleId === 0) {
            store.commit('userRoles/initRole', roleId);
            privileges = store.state.userRoles.privileges[roleId];
        } else {
            // When a new role is first created, it's privileges must be assigned to the store state
            store.dispatch('userRoles/setPrivileges');
        }
    }

    /**
     * Bridge the privileges into the form's local state.
     * Presetting this ensures that modifications in the child components
     * trigger the main form's dirty/modified tracking.
     */
    store.commit(`${props.formId}/presetData`, {
        name: 'roles',
        value: privileges
    });
});
</script>

<template>
    <div>
        <div
            v-for="rolesGroup in rolesGroups"
            :key="rolesGroup"
            class="mb-4"
        >
            <div class="row">
                <label class="col-sm-3 form-label">{{ rolesGroup }}</label>
                <div class="col-sm-9 control-wrapper">
                    <FormControlUsersGroupsRolesGroup
                        :form-id="formId"
                        :roles-group="rolesGroup"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Scoped styles can be added here if specific alignment is needed */
</style>